import type { QueryClient } from '@tanstack/react-query'
import { auth as userAuth } from '../../api/auth/auth'
import { getMyPermissions } from '../../api/settings/pagePermissions'
import { fetchEmployee } from '../../api/workforce/workforce'
import type { User } from '../../types/auth/auth'

export const shellQueryStaleTime = 60 * 1000

export const authUserQueryOptions = {
  queryKey: ['authUser'] as const,
  queryFn: userAuth,
  staleTime: shellQueryStaleTime,
}

export const employeeMinimalQueryOptions = (employeeId?: string | number | null) => ({
  queryKey: ['employee-minimal', String(employeeId)] as const,
  queryFn: () => fetchEmployee(String(employeeId), { minimal: true }),
  staleTime: shellQueryStaleTime,
  enabled: !!employeeId,
})

export const currentSelfQueryOptions = (queryClient: QueryClient, enabled = true) => ({
  queryKey: ['current-self'] as const,
  enabled,
  staleTime: shellQueryStaleTime,
  queryFn: async () => {
    const authUser = await queryClient.fetchQuery({
      ...authUserQueryOptions,
      staleTime: 0,
    }) as User

    return {
      authUser,
      employee: null,
      employeeId: authUser?.employee_id ? String(authUser.employee_id) : (authUser?.id ? String(authUser.id) : null),
    }
  },
})

export const setCachedChatbotMode = (
  queryClient: QueryClient,
  chatbotMode: 'user' | 'admin',
) => {
  queryClient.setQueryData<User | undefined>(['authUser'], (old) => (
    old ? { ...old, chatbot_mode: chatbotMode } : old
  ))

  queryClient.setQueryData<
    | {
      authUser: User
      employee: unknown
      employeeId: string | null
    }
    | undefined
  >(['current-self'], (old) => (
    old
      ? {
          ...old,
          authUser: {
            ...old.authUser,
            chatbot_mode: chatbotMode,
          },
        }
      : old
  ))
}

export const myPermissionsQueryOptions = (enabled = true) => ({
  queryKey: ['my-permissions'] as const,
  queryFn: getMyPermissions,
  staleTime: shellQueryStaleTime,
  enabled,
})
